import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import { ServerTarget } from '../models';
import { MetaCoreService, ServerStatusService } from '../services/';
import { MetaCoreActions } from './meta-core.actions';

const SERVER_TARGETS: ServerTarget[] = ['http', 'ws'];

@Injectable()
export class MetaCoreEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly metaCoreService: MetaCoreService = inject(MetaCoreService);
  private readonly serverStatusService: ServerStatusService =
    inject(ServerStatusService);

  getMeta$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMeta),
      mergeMap(({ params }) => this.metaCoreService.requestMetaData(params)),
      map(response => MetaCoreActions.loadMetaSuccess({ meta: response.res })),
      catchError(error => of(MetaCoreActions.loadMetaError({ error })))
    )
  );

  getMetaTimezones$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaTimezones),
      map(() => MetaCoreActions.loadMeta({ params: ['tzs'] }))
    )
  );

  getMetaRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaRoles),
      map(() => MetaCoreActions.loadMeta({ params: ['ptlRoleGroups'] }))
    )
  );

  getMetaLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaLanguages),
      map(() => MetaCoreActions.loadMeta({ params: ['lngs'] }))
    )
  );

  getMetaTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MetaCoreActions.loadMetaGuiDomain,
        MetaCoreActions.loadMetaGuiOnlineBookDomain,
        MetaCoreActions.loadMetaMobileTitle,
        MetaCoreActions.loadMetaGuiShrDomain,
        MetaCoreActions.loadMetaAuthGuiDomain,
        MetaCoreActions.loadMetaDocGuiDomain
      ),
      map(() => MetaCoreActions.loadMeta({ params: ['ptl'] }))
    )
  );

  getMetaPhones$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaPhonesCountryCodes),
      map(() => MetaCoreActions.loadMeta({ params: ['phns'] }))
    )
  );

  getMetaWebsocketPingPongTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaWebsocket),
      map(() => MetaCoreActions.loadMeta({ params: ['wsPingPong'] }))
    )
  );

  getColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadMetaColors),
      map(() => MetaCoreActions.loadMeta({ params: ['colors'] }))
    )
  );

  getServerStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetaCoreActions.loadServerStatus),
      mergeMap(() => SERVER_TARGETS),
      mergeMap(target =>
        this.serverStatusService.getServerStatus(target).pipe(
          map(status =>
            MetaCoreActions.loadServerStatusSuccess({ target, status })
          ),
          catchError(error =>
            of(MetaCoreActions.loadServerStatusFailure({ target, error }))
          )
        )
      )
    )
  );
}
